import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Download } from "lucide-react";

export const Route = createFileRoute("/harikaradmin/drinks-report")({
  head: () => ({ meta: [{ title: "İçecek Raporu — Admin" }] }),
  component: DrinksReportPage,
});

interface VoucherRow {
  id: string;
  drink_id: string;
  event_id: string | null;
  user_id: string;
  created_at: string;
  redeemed_at: string | null;
}
interface DrinkLite {
  id: string;
  name: string;
  price_kurus: number;
  event_id: string | null;
}
interface ProfileLite {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}
interface EventLite {
  id: string;
  title: string;
  starts_at: string;
}

const TZ = "Europe/Istanbul";
const todayIso = () => new Date().toISOString().slice(0, 10);
const daysAgoIso = (n: number) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// "İşletme günü" 06:00'da başlar; 00:00-05:59 arası önceki güne sayılır.
const DAY_START_HOUR = 6;

// Get hour (0-23) in Istanbul TZ from ISO string
function trHour(iso: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    hour: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const h = parts.find((p) => p.type === "hour")?.value ?? "0";
  return parseInt(h, 10) % 24;
}
// Saat kovası 0..23 -> gerçek saat: (i + 6) % 24 (06:00 başlangıç)
function hourBucket(iso: string): number {
  return (trHour(iso) - DAY_START_HOUR + 24) % 24;
}
function trWeekdayRaw(iso: string): number {
  // 0=Mon ... 6=Sun
  const wd = new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    weekday: "short",
  }).format(new Date(iso));
  const map: Record<string, number> = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
  return map[wd] ?? 0;
}
// İşletme gününe göre haftanın günü (00:00-05:59 önceki güne sayılır)
function trWeekday(iso: string): number {
  const wd = trWeekdayRaw(iso);
  const h = trHour(iso);
  return h < DAY_START_HOUR ? (wd - 1 + 7) % 7 : wd;
}
const WD_LABELS = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const HOUR_LABEL = (bucket: number) => (bucket + DAY_START_HOUR) % 24;

function DrinksReportPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [from, setFrom] = useState(daysAgoIso(30));
  const [to, setTo] = useState(todayIso());
  const [vouchers, setVouchers] = useState<VoucherRow[]>([]);
  const [drinks, setDrinks] = useState<Record<string, DrinkLite>>({});
  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [events, setEvents] = useState<Record<string, EventLite>>({});
  const [busy, setBusy] = useState(false);
  const [basis, setBasis] = useState<"purchased" | "redeemed">("purchased");

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) void navigate({ to: "/" });
  }, [loading, user, isAdmin, navigate]);

  const load = async () => {
    setBusy(true);
    // İşletme günü 06:00'da başlar — "to" günü Cmt ise Pazar 05:59'a kadar dahil et
    const fromTs = new Date(from + "T00:00:00").toISOString();
    const toDate = new Date(to + "T00:00:00");
    toDate.setDate(toDate.getDate() + 1);
    toDate.setHours(DAY_START_HOUR - 1, 59, 59, 999);
    const toTs = toDate.toISOString();
    const col = basis === "purchased" ? "created_at" : "redeemed_at";
    let q = supabase
      .from("drink_vouchers")
      .select("id, drink_id, event_id, user_id, created_at, redeemed_at")
      .gte(col, fromTs)
      .lte(col, toTs)
      .order(col, { ascending: false })
      .limit(10000);
    if (basis === "redeemed") q = q.not("redeemed_at", "is", null);
    const { data, error } = await q;
    if (error) {
      setBusy(false);
      return;
    }
    const list = (data as VoucherRow[]) ?? [];
    setVouchers(list);

    const drinkIds = Array.from(new Set(list.map((v) => v.drink_id)));
    const userIds = Array.from(new Set(list.map((v) => v.user_id)));
    const eventIds = Array.from(
      new Set(list.map((v) => v.event_id).filter((x): x is string => !!x)),
    );

    const [dRes, pRes, eRes] = await Promise.all([
      drinkIds.length
        ? supabase.from("drinks").select("id, name, price_kurus, event_id").in("id", drinkIds)
        : Promise.resolve({ data: [] as DrinkLite[] }),
      userIds.length
        ? supabase.from("profiles").select("id, first_name, last_name, email").in("id", userIds)
        : Promise.resolve({ data: [] as ProfileLite[] }),
      eventIds.length
        ? supabase.from("events").select("id, title, starts_at").in("id", eventIds)
        : Promise.resolve({ data: [] as EventLite[] }),
    ]);

    const dMap: Record<string, DrinkLite> = {};
    for (const d of (dRes.data as DrinkLite[]) ?? []) dMap[d.id] = d;
    setDrinks(dMap);
    const pMap: Record<string, ProfileLite> = {};
    for (const p of (pRes.data as ProfileLite[]) ?? []) pMap[p.id] = p;
    setProfiles(pMap);
    const eMap: Record<string, EventLite> = {};
    for (const e of (eRes.data as EventLite[]) ?? []) eMap[e.id] = e;
    setEvents(eMap);

    setBusy(false);
  };

  useEffect(() => {
    if (user && isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin, basis]);

  const timeField = (v: VoucherRow) =>
    basis === "purchased" ? v.created_at : (v.redeemed_at ?? v.created_at);

  const totals = useMemo(() => {
    let revenueKurus = 0;
    let redeemedCount = 0;
    const userSet = new Set<string>();
    for (const v of vouchers) {
      const d = drinks[v.drink_id];
      if (d) revenueKurus += d.price_kurus;
      if (v.redeemed_at) redeemedCount += 1;
      userSet.add(v.user_id);
    }
    const redemptionRate =
      vouchers.length > 0 ? Math.round((redeemedCount / vouchers.length) * 100) : 0;
    return {
      count: vouchers.length,
      revenueKurus,
      redeemedCount,
      redemptionRate,
      uniqueUsers: userSet.size,
    };
  }, [vouchers, drinks]);

  const byHour = useMemo(() => {
    const arr = Array.from({ length: 24 }, () => 0);
    for (const v of vouchers) arr[hourBucket(timeField(v))] += 1;
    const max = Math.max(1, ...arr);
    return { arr, max };
  }, [vouchers, basis]);

  const byWeekday = useMemo(() => {
    const arr = Array.from({ length: 7 }, () => 0);
    for (const v of vouchers) arr[trWeekday(timeField(v))] += 1;
    const max = Math.max(1, ...arr);
    return { arr, max };
  }, [vouchers, basis]);

  const byDrink = useMemo(() => {
    const map = new Map<string, { count: number; revenueKurus: number }>();
    for (const v of vouchers) {
      const d = drinks[v.drink_id];
      const cur = map.get(v.drink_id) ?? { count: 0, revenueKurus: 0 };
      cur.count += 1;
      cur.revenueKurus += d?.price_kurus ?? 0;
      map.set(v.drink_id, cur);
    }
    return Array.from(map.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [vouchers, drinks]);

  const byEvent = useMemo(() => {
    const map = new Map<string, { count: number; revenueKurus: number }>();
    for (const v of vouchers) {
      const key = v.event_id ?? "_none";
      const d = drinks[v.drink_id];
      const cur = map.get(key) ?? { count: 0, revenueKurus: 0 };
      cur.count += 1;
      cur.revenueKurus += d?.price_kurus ?? 0;
      map.set(key, cur);
    }
    return Array.from(map.entries())
      .map(([id, v]) => ({ id, ...v }))
      .sort((a, b) => b.count - a.count);
  }, [vouchers, drinks]);

  const byUser = useMemo(() => {
    const map = new Map<
      string,
      { count: number; revenueKurus: number; drinkCounts: Map<string, number> }
    >();
    for (const v of vouchers) {
      const d = drinks[v.drink_id];
      const cur = map.get(v.user_id) ?? {
        count: 0,
        revenueKurus: 0,
        drinkCounts: new Map<string, number>(),
      };
      cur.count += 1;
      cur.revenueKurus += d?.price_kurus ?? 0;
      cur.drinkCounts.set(v.drink_id, (cur.drinkCounts.get(v.drink_id) ?? 0) + 1);
      map.set(v.user_id, cur);
    }
    return Array.from(map.entries())
      .map(([id, v]) => {
        let favId: string | null = null;
        let favCount = 0;
        for (const [dId, c] of v.drinkCounts) {
          if (c > favCount) {
            favCount = c;
            favId = dId;
          }
        }
        return {
          id,
          count: v.count,
          revenueKurus: v.revenueKurus,
          favoriteDrinkId: favId,
          favoriteCount: favCount,
        };
      })
      .sort((a, b) => b.count - a.count)
      .slice(0, 50);
  }, [vouchers, drinks]);

  const favoriteRanking = useMemo(() => {
    const map = new Map<string, number>();
    for (const u of byUser) {
      if (!u.favoriteDrinkId) continue;
      map.set(u.favoriteDrinkId, (map.get(u.favoriteDrinkId) ?? 0) + 1);
    }
    return Array.from(map.entries())
      .map(([id, users]) => ({ id, users }))
      .sort((a, b) => b.users - a.users);
  }, [byUser]);

  const tl = (k: number) => (k / 100).toLocaleString("tr-TR", { maximumFractionDigits: 2 });

  const exportCsv = () => {
    const headers = [
      "Tarih (TR)",
      "İçecek",
      "Üye",
      "E-posta",
      "Etkinlik",
      "Birim Fiyat ₺",
      "Durum",
    ];
    const lines = [headers.join(",")];
    const dtf = new Intl.DateTimeFormat("tr-TR", {
      timeZone: TZ,
      dateStyle: "short",
      timeStyle: "short",
    });
    for (const v of vouchers) {
      const d = drinks[v.drink_id];
      const p = profiles[v.user_id];
      const e = v.event_id ? events[v.event_id] : null;
      const cells = [
        `"${dtf.format(new Date(timeField(v)))}"`,
        `"${(d?.name ?? "").replace(/"/g, '""')}"`,
        `"${(p ? `${p.first_name} ${p.last_name}`.trim() : "").replace(/"/g, '""')}"`,
        `"${p?.email ?? ""}"`,
        `"${(e?.title ?? "").replace(/"/g, '""')}"`,
        tl(d?.price_kurus ?? 0),
        v.redeemed_at ? "Kullanıldı" : "Bekliyor",
      ];
      lines.push(cells.join(","));
    }
    const blob = new Blob(["\uFEFF" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `icecek-raporu-${basis}-${from}_${to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !user || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-6xl px-4 py-10 text-center text-muted-foreground">
          Yükleniyor...
        </div>
      </div>
    );
  }

  const peakHour = byHour.arr.indexOf(Math.max(...byHour.arr));
  const peakWd = byWeekday.arr.indexOf(Math.max(...byWeekday.arr));

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10">
        <h1 className="mb-6 text-3xl font-bold tracking-tight">Admin</h1>
        <AdminTabs />

        <section className="rounded-2xl border border-border bg-card p-6 shadow-card">
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="from">Başlangıç</Label>
              <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="to">Bitiş</Label>
              <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            <div>
              <Label>Kriter</Label>
              <div className="inline-flex rounded-lg border border-border p-1">
                <button
                  className={`rounded-md px-3 py-1.5 text-sm ${basis === "purchased" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  onClick={() => setBasis("purchased")}
                >
                  Satın alma
                </button>
                <button
                  className={`rounded-md px-3 py-1.5 text-sm ${basis === "redeemed" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}
                  onClick={() => setBasis("redeemed")}
                >
                  Tüketim
                </button>
              </div>
            </div>
            <Button onClick={() => void load()} disabled={busy}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Uygula"}
            </Button>
            <Button variant="outline" onClick={exportCsv} disabled={vouchers.length === 0}>
              <Download className="mr-1.5 h-4 w-4" /> CSV
            </Button>
            <div className="ml-auto flex flex-wrap gap-2 text-xs">
              {[7, 30, 90].map((n) => (
                <button
                  key={n}
                  className="rounded-full border border-border px-3 py-1 hover:bg-muted"
                  onClick={() => {
                    setFrom(daysAgoIso(n));
                    setTo(todayIso());
                  }}
                >
                  Son {n} gün
                </button>
              ))}
            </div>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            {basis === "purchased"
              ? "Satın alma zamanı baz alınır (vouchers oluşturulma tarihi)."
              : "Garson tarafından bara teslim zamanı baz alınır (yalnızca kullanılmış voucher'lar)."}
          </p>
        </section>

        <section className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Toplam içecek" value={totals.count.toLocaleString("tr-TR")} />
          <StatCard label="Ciro" value={`${tl(totals.revenueKurus)} ₺`} tone="success" />
          <StatCard label="Tekil üye" value={totals.uniqueUsers.toLocaleString("tr-TR")} />
          <StatCard
            label="Kullanım oranı"
            value={`%${totals.redemptionRate}`}
            hint={`${totals.redeemedCount.toLocaleString("tr-TR")} / ${totals.count.toLocaleString("tr-TR")}`}
          />
        </section>

        <section className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="text-lg font-semibold">Saate göre dağılım</h2>
              <span className="text-xs text-muted-foreground">
                Pik: {String(HOUR_LABEL(peakHour)).padStart(2, "0")}:00
              </span>
            </div>
            <div className="flex h-48 items-stretch gap-1">
              {byHour.arr.map((v, i) => (
                <div key={i} className="group flex h-full flex-1 flex-col items-center justify-end">
                  <div className="mb-1 text-[9px] text-muted-foreground">{v > 0 ? v : ""}</div>
                  <div
                    className="w-full rounded-t bg-primary/80 transition-all group-hover:bg-primary"
                    style={{ height: `${(v / byHour.max) * 100}%`, minHeight: v > 0 ? 2 : 0 }}
                    title={`${String(HOUR_LABEL(i)).padStart(2, "0")}:00 — ${v}`}
                  />
                  <span className="mt-1 text-[9px] text-muted-foreground">
                    {i % 3 === 0 ? HOUR_LABEL(i) : ""}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <div className="mb-2 flex items-baseline justify-between">
              <h2 className="text-lg font-semibold">Haftanın gününe göre</h2>
              <span className="text-xs text-muted-foreground">Pik: {WD_LABELS[peakWd]}</span>
            </div>
            <div className="flex h-48 items-stretch gap-2">
              {byWeekday.arr.map((v, i) => (
                <div key={i} className="flex h-full flex-1 flex-col items-center justify-end">
                  <div className="mb-1 text-[10px] text-muted-foreground">{v}</div>
                  <div
                    className="w-full rounded-t bg-accent"
                    style={{ height: `${(v / byWeekday.max) * 100}%`, minHeight: v > 0 ? 2 : 0 }}
                    title={`${WD_LABELS[i]} — ${v}`}
                  />
                  <span className="mt-1 text-xs text-muted-foreground">{WD_LABELS[i]}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mt-6 grid gap-6 lg:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h2 className="mb-3 text-lg font-semibold">En çok tüketilen içecekler</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">İçecek</th>
                    <th className="py-2 px-3 text-right">Adet</th>
                    <th className="py-2 pl-3 text-right">Ciro</th>
                  </tr>
                </thead>
                <tbody>
                  {byDrink.slice(0, 20).map((row) => {
                    const d = drinks[row.id];
                    return (
                      <tr key={row.id} className="border-b border-border/50">
                        <td className="py-2 pr-3 font-medium">{d?.name ?? "—"}</td>
                        <td className="py-2 px-3 text-right">{row.count}</td>
                        <td className="py-2 pl-3 text-right">{tl(row.revenueKurus)} ₺</td>
                      </tr>
                    );
                  })}
                  {byDrink.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-6 text-center text-muted-foreground">
                        Veri yok.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
            <h2 className="mb-3 text-lg font-semibold">Etkinlik bazında</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Etkinlik</th>
                    <th className="py-2 px-3 text-right">Adet</th>
                    <th className="py-2 pl-3 text-right">Ciro</th>
                  </tr>
                </thead>
                <tbody>
                  {byEvent.map((row) => {
                    const e = row.id === "_none" ? null : events[row.id];
                    return (
                      <tr key={row.id} className="border-b border-border/50">
                        <td className="py-2 pr-3 font-medium">{e?.title ?? "Etkinlik dışı"}</td>
                        <td className="py-2 px-3 text-right">{row.count}</td>
                        <td className="py-2 pl-3 text-right">{tl(row.revenueKurus)} ₺</td>
                      </tr>
                    );
                  })}
                  {byEvent.length === 0 && (
                    <tr>
                      <td colSpan={3} className="py-6 text-center text-muted-foreground">
                        Veri yok.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="mb-1 text-lg font-semibold">Favori içecek sıralaması</h2>
          <p className="mb-3 text-xs text-muted-foreground">
            Her üyenin en çok aldığı içecek "favori" sayılır. Aşağıda kaç üyenin favorisi olduğu
            gösterilir.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">İçecek</th>
                  <th className="py-2 pl-3 text-right">Favori üye sayısı</th>
                </tr>
              </thead>
              <tbody>
                {favoriteRanking.slice(0, 20).map((row) => (
                  <tr key={row.id} className="border-b border-border/50">
                    <td className="py-2 pr-3 font-medium">{drinks[row.id]?.name ?? "—"}</td>
                    <td className="py-2 pl-3 text-right">{row.users}</td>
                  </tr>
                ))}
                {favoriteRanking.length === 0 && (
                  <tr>
                    <td colSpan={2} className="py-6 text-center text-muted-foreground">
                      Veri yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
          <h2 className="mb-3 text-lg font-semibold">En aktif üyeler (top 50)</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[700px] text-sm">
              <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                <tr>
                  <th className="py-2 pr-3">Üye</th>
                  <th className="py-2 px-3">Favori içecek</th>
                  <th className="py-2 px-3 text-right">İçecek</th>
                  <th className="py-2 pl-3 text-right">Harcama</th>
                </tr>
              </thead>
              <tbody>
                {byUser.map((row) => {
                  const p = profiles[row.id];
                  const fav = row.favoriteDrinkId ? drinks[row.favoriteDrinkId] : null;
                  return (
                    <tr key={row.id} className="border-b border-border/50">
                      <td className="py-2 pr-3">
                        <div className="font-medium">
                          {p ? `${p.first_name} ${p.last_name}`.trim() || p.email : "—"}
                        </div>
                        <div className="text-xs text-muted-foreground">{p?.email}</div>
                      </td>
                      <td className="py-2 px-3">
                        {fav ? (
                          <div>
                            <div className="font-medium">{fav.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {row.favoriteCount} kez
                            </div>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="py-2 px-3 text-right">{row.count}</td>
                      <td className="py-2 pl-3 text-right">{tl(row.revenueKurus)} ₺</td>
                    </tr>
                  );
                })}
                {byUser.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-muted-foreground">
                      Veri yok.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: string;
  hint?: string;
  tone?: "default" | "success" | "warn";
}) {
  const color =
    tone === "success"
      ? "text-success"
      : tone === "warn"
        ? "text-destructive"
        : "text-foreground";
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${color}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}
